import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, takeEvery } from "redux-saga/effects";
import { showAlert } from "src/components/UI/CustomAlert";
import { CartItem, CartState } from "src/types/cartTypes";
import { normalizeCartCurrencyCopy } from "src/utils/helpers";
import {
  handleGetCartApi,
  handleGetTaxApi,
  handleUpdateCart,
} from "../apis/cart";
import {
  selectCartItems,
  selectTotalCartQuantity,
  selectTotalPrice,
} from "../selectors/cart";
import { selectUserToken } from "../selectors/user";
import {
  addCouponCode,
  addCouponCodeFailure,
  addItem,
  clearCartAction,
  clearCouponErrorMessage,
  createReOrder,
  getCart,
  getCartFailure,
  getCartSuccess,
  getTax,
  getTaxFailure,
  getTaxSuccess,
  refreshCart,
  removeItem,
  updateItemQuantity,
  updateItems,
} from "../slices/cart";

const getApiErrorMessage = (error: any): string => {
  const raw = error?.message;
  if (Array.isArray(raw))
    return normalizeCartCurrencyCopy(raw[0] || "Something went wrong");
  if (typeof raw === "string" && raw.trim().length > 0)
    return normalizeCartCurrencyCopy(raw);
  if (typeof error === "string" && error.trim().length > 0)
    return normalizeCartCurrencyCopy(error);
  return "Something went wrong";
};

type UpdateCartStateOptions = {
  couponCode?: string;
  allowLocalFallbackOnProcessingError?: boolean;
  /** PUT body items; defaults to updatedItems (full local cart). */
  apiItems?: CartItem[];
  replaceItems?: boolean;
  removedLineIndexes?: number[];
};

// Utility to update cart and dispatch state
function* updateCartState(
  updatedItems: CartItem[],
  totalPrice: number,
  totalQuantity: number,
  options: UpdateCartStateOptions = {},
): Generator<any, boolean, any> {
  const {
    couponCode,
    allowLocalFallbackOnProcessingError = false,
    apiItems = updatedItems,
    replaceItems,
    removedLineIndexes,
  } = options;

  let payload: any = {
    items: updatedItems,
    totalPrice: totalPrice,
    totalQuantity: totalQuantity,
    currency: "CAD",
  };
  try {
    const isUserLoggedIn = Boolean(yield select(selectUserToken));
    if (isUserLoggedIn) {
      payload = yield call(handleUpdateCart, {
        updatedItems: apiItems,
        couponCode,
        replaceItems,
        removedLineIndexes,
      });
    }

    const serverTotalPrice = Number(payload?.totalPrice ?? totalPrice);
    const serverTotalQuantity = Number(payload?.totalQuantity ?? totalQuantity);

    yield put(
      updateItems({
        items: payload?.items ?? updatedItems,
        totalPrice: Number(serverTotalPrice.toFixed(2)),
        totalQuantity: Number(serverTotalQuantity.toFixed(2)),
        discount: payload?.discount,
        couponCode: payload?.couponCode,
      } as CartState),
    );
    return true;
  } catch (error: any) {
    const errorMessage = String(error?.message || "").toLowerCase();
    const isProcessingStatusGuardError = errorMessage.includes(
      "cart can be cleared only when latest order status is processing",
    );

    // Allow manual delete flow to proceed in UI if backend cart-clear guard blocks update.
    if (allowLocalFallbackOnProcessingError && isProcessingStatusGuardError) {
      yield put(
        updateItems({
          items: updatedItems,
          totalPrice: Number(totalPrice.toFixed(2)),
          totalQuantity: Number(totalQuantity.toFixed(2)),
          discount: 0,
          couponCode: undefined,
        } as CartState),
      );
      return true;
    }

    yield put(refreshCart());
    const cartErr =
      typeof error?.message === "string"
        ? normalizeCartCurrencyCopy(error.message)
        : error?.message;
    showAlert("error", cartErr);
    return false;
  }
}

// Handle Adding an Item to the Cart
function* handleAddItem(
  action: PayloadAction<CartItem>,
): Generator<any, void, any> {
  let totalPrice: number = yield select(selectTotalPrice);
  let totalQuantity: number = yield select(selectTotalCartQuantity);
  const items: CartItem[] = yield select(selectCartItems);

  const updatedItems = [...items, action.payload];

  totalPrice += action.payload.price;
  totalQuantity += action.payload.quantity;
  const updated = (yield* updateCartState(
    updatedItems,
    totalPrice,
    totalQuantity,
    { apiItems: [action.payload] },
  )) as boolean;
  if (updated) {
    showAlert("success", "Added to Cart Successfully");
  }
}

function sumCartTotals(items: CartItem[]) {
  return items.reduce(
    (acc, item) => {
      const quantity = Number(item.quantity) || 0;
      const price = Number(item.price) || 0;
      acc.totalQuantity += quantity;
      acc.totalPrice += price * quantity;
      return acc;
    },
    { totalQuantity: 0, totalPrice: 0 },
  );
}

// Handle Removing an Item Completely from the Cart
function* handleRemoveItem(
  action: PayloadAction<number>,
): Generator<any, void, any> {
  const items: CartItem[] = yield select(selectCartItems);
  const updatedItems = items.filter((_, index) => index !== action.payload);
  const { totalPrice, totalQuantity } = sumCartTotals(updatedItems);

  yield* updateCartState(updatedItems, totalPrice, totalQuantity, {
    replaceItems: true,
  });
}

// Generic Handler for Quantity Changes (Increase / Decrease)
function* handleQuantityChange(
  action: PayloadAction<{ id: number; change: number }>,
): Generator<any, void, any> {
  const items: CartItem[] = yield select(selectCartItems);
  const updatedItems = items.map((item, index) =>
    index === action.payload.id
      ? { ...item, quantity: action.payload.change }
      : item,
  );
  const { totalPrice, totalQuantity } = sumCartTotals(updatedItems);

  yield* updateCartState(updatedItems, totalPrice, totalQuantity, {
    replaceItems: true,
  });
}

function* handleIncreaseByOne(action: PayloadAction<any>) {
  yield* handleQuantityChange({
    payload: { id: action.payload.id, change: action.payload.quantity },
  } as PayloadAction<{ id: number; change: number }>);
}

function* handleGetTax(action: PayloadAction<any>): Generator {
  try {
    const items: any = yield call(handleGetTaxApi, action.payload);
    yield put(getTaxSuccess(items));
  } catch (error: any) {
    yield put(getTaxFailure(error.message || "Failed to fetch products"));
  }
}

function* handleCreateReOrder(action: PayloadAction<any>): Generator {
  try {
    const {
      items = [],
      totalQuantity = 0,
      totalPrice = 0,
    } = action.payload?.payload ?? {};
    const reorderItems: CartItem[] = Array.isArray(items)
      ? (items as CartItem[])
      : [];
    const selectedItems = (yield select(selectCartItems)) as unknown;
    const currentItems: CartItem[] = Array.isArray(selectedItems)
      ? (selectedItems as CartItem[])
      : [];
    const currentTotalPrice = Number((yield select(selectTotalPrice)) ?? 0);
    const currentTotalQuantity = Number(
      (yield select(selectTotalCartQuantity)) ?? 0,
    );

    // Re-order should append to current cart, not replace it.
    const mergedItems = [...currentItems, ...reorderItems];
    const mergedTotalPrice = currentTotalPrice + Number(totalPrice || 0);
    const mergedTotalQuantity =
      currentTotalQuantity + Number(totalQuantity || 0);

    yield* updateCartState(mergedItems, mergedTotalPrice, mergedTotalQuantity, {
      apiItems: reorderItems,
    });
    action.payload?.navigateToCheckout();
  } catch (error: any) {
    console.log(error, "error");
  }
}

function* handleAddCouponCode(action: PayloadAction<any>): Generator {
  try {
    const items = (yield select(selectCartItems)) as CartItem[];
    const totalPrice = Number((yield select(selectTotalPrice)) ?? 0);
    const totalQuantity = Number((yield select(selectTotalCartQuantity)) ?? 0);
    const payload: any = yield call(handleUpdateCart, {
      updatedItems: items,
      couponCode: action?.payload,
      replaceItems: true,
    });

    yield put(
      updateItems({
        items: payload?.items ?? items,
        totalPrice: Number(payload?.totalPrice?.toFixed?.(2) ?? totalPrice),
        totalQuantity: Number(
          payload?.totalQuantity?.toFixed?.(2) ?? totalQuantity,
        ),
        discount: payload?.discount ?? 0,
        couponCode: payload?.couponCode,
      } as CartState),
    );
    yield put(clearCouponErrorMessage());

    const hasAppliedCoupons = Array.isArray(action?.payload)
      ? action.payload.length > 0
      : Boolean(action?.payload);
    if (hasAppliedCoupons) showAlert("success", "Coupon Added Successfully");
  } catch (error: any) {
    const message = getApiErrorMessage(error);
    yield put(addCouponCodeFailure(message));
    showAlert("error", message);
  }
}

function* handleGetCart(action: PayloadAction<any>): Generator {
  try {
    const items: any = yield call(handleGetCartApi, action.payload);
    yield put(getCartSuccess(items));
  } catch (error: any) {
    yield put(getCartFailure(error.message || "Failed to fetch products"));
  }
}

function* handleClearCartAction(): Generator<any, void, any> {
  yield* updateCartState([], 0, 0, {
    apiItems: [],
    replaceItems: true,
  });
}

// Watcher Saga for Handling Cart Actions
export default function* cartSaga() {
  yield takeEvery(addItem.type, handleAddItem);
  yield takeEvery(removeItem.type, handleRemoveItem);
  yield takeEvery(updateItemQuantity.type, handleIncreaseByOne);
  yield takeEvery(getTax.type, handleGetTax);
  yield takeEvery(createReOrder.type, handleCreateReOrder);
  yield takeEvery(addCouponCode.type, handleAddCouponCode);
  yield takeEvery(getCart.type, handleGetCart);
  yield takeEvery(clearCartAction.type, handleClearCartAction);
}
